import React from 'react'

import { Box, Button, Form, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import { motion } from 'framer-motion'
import { useOnClickOutside } from 'usehooks-ts'
import { v4 as uuid } from 'uuid'

import TextArea from 'modules/core/components/TextArea'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import i18n from 'modules/core/lib/i18n'

import QuestionTypeSelector from 'modules/questionnaires/components/QuestionTypeSelector'
import { handleFormSubmit } from 'modules/questionnaires/lib/formHelpers'
import {
  getQuestionTypeSelector,
  isQuestionChoice,
  isQuestionRating,
} from 'modules/questionnaires/lib/helpers'
import {
  QuestionTypeSelector as QuestionTypeSelectorType,
  Question as QuestionValue,
  Translations,
} from 'modules/questionnaires/lib/types'

import MultipleChoiceConfiguration from './MultipleChoiceConfiguration'
import QuestionHeader from './QuestionHeader'

type Handler = (question: QuestionValue) => Promise<QuestionValue> | void

type Props = {
  question?: QuestionValue
  editing?: boolean
  disabled?: boolean
  locked?: boolean
  withDescription?: boolean
  isDragging?: boolean
  translations: Translations['question']
  onSave?: Handler
  onDuplicate?: Handler
  onDelete?: Handler
  onSaveAndDuplicate?: (questions: QuestionValue[]) => Promise<QuestionValue[]> | void
}

const Question: React.FC<Props> = ({
  question,
  editing = false,
  disabled = false,
  locked = false,
  withDescription = false,
  isDragging = false,
  translations,
  onSave,
  onDuplicate,
  onDelete,
  onSaveAndDuplicate,
}) => {
  const ref = React.useRef(null)

  const [isTypeSelectionOpen, toggleTypeSelection] = React.useState(false)
  const [isEditing, toggleEditing] = React.useState(editing)

  const form = useForm<QuestionValue>({
    defaultValues: {
      uuid: question?.uuid || uuid(),
      answerType: question?.answerType || 'text',
      title: question?.title || '',
      description: question?.description || undefined,
      mandatory: question?.mandatory || false,
      withComment: question?.withComment || false,
      scale: question?.scale || [],
      maxChoices: question?.maxChoices || 0,
      choiceOptions: question?.choiceOptions || [],
    },
    validateOnChange: false,
    validateOnBlur: false,
    validate: (data) => {
      const errors: FormErrors<QuestionValue> = {}
      if (!data.title) {
        errors.title = translations.validations.title.required
      }

      if (isQuestionChoice(data)) {
        if (data.choiceOptions.length < 2) {
          errors.answerType = translations.validations.answer_type.choice_options.min_two_options
        }

        const choiceErrors: string[] = data.choiceOptions
          .map((choice, index) => {
            if (!choice) return translations.validations.options.required
            if (data.choiceOptions.indexOf(choice) !== index)
              return translations.validations.options.repeated
            return ''
          })
          .filter(Boolean)

        if (!!choiceErrors.length) {
          errors.choiceOptions = choiceErrors
        }
      }

      return errors
    },
    onSubmit: (data) => {
      toggleEditing(false)

      if (!form.dirty || !onSave) {
        return handleFormSubmit(() => {})
      }

      return handleFormSubmit(onSave, setMaxChoices(data))
    },
  })

  const setMaxChoices = (data: QuestionValue) => {
    const isChoiceType = data.answerType === 'multiple_choice'
    if (!isChoiceType || data.maxChoices === 1) {
      return data
    }

    return { ...data, maxChoices: data.choiceOptions.length }
  }

  const handleDuplication = async () => {
    const validation = form.dirty || question === undefined ? await form.validateForm() : {}

    if (Object.keys(validation).length) return

    const duplicated = setMaxChoices({
      ...form.values,
      uuid: uuid(),
      title: `${form.values.title} ${translations.copy_placeholder}`,
    })

    if (question === undefined && onSaveAndDuplicate) {
      onSaveAndDuplicate([form.values, duplicated])
    } else {
      onDuplicate?.(duplicated)
    }

    toggleEditing(false)
  }

  const handleClickOutside = () => {
    if (isTypeSelectionOpen) return
    form.handleSubmit()
  }

  useOnClickOutside(ref, handleClickOutside)

  const handleAnswerTypeChange = (value: QuestionTypeSelectorType) => {
    const isChoiceType = value === 'single_choice' || value === 'multiple_choice'
    if (!isChoiceType) {
      form.setFieldValue('answerType', value)
      form.setFieldValue('maxChoices', null)
      return
    }

    const maxChoices = value === 'single_choice' ? 1 : null
    form.setFieldValue('maxChoices', maxChoices)
    form.setFieldValue('answerType', 'multiple_choice')

    if (form.values.choiceOptions.length === 0) {
      form.setFieldValue('choiceOptions', ['', ''])
    }
  }

  return (
    <>
      {!isEditing && (
        <motion.div layout>
          <QuestionHeader
            translations={translations}
            question={form.values}
            disabled={disabled}
            locked={locked}
            duplicateDisabled={(question === undefined && !onSaveAndDuplicate) || disabled}
            onEdit={() => !isDragging && toggleEditing(true)}
            onDuplicate={handleDuplication}
          />
        </motion.div>
      )}

      {isEditing && (
        <Box
          ref={ref}
          width="full"
          border={{ all: { color: 'grey400', width: 's1', style: 'solid' } }}
          borderRadius={{ all: 'abs008' }}
          paddingY="s16"
          paddingLeft="s32"
          paddingRight="s24"
          background="white"
        >
          <Form padding="s0">
            <Box gap="s24">
              <TextArea
                mandatory
                readonly={disabled}
                label={translations.label}
                value={form.values.title}
                error={form.errors.title}
                onChange={(value) => form.setFieldValue('title', value, form.submitCount > 0)}
                placeholder={translations.placeholder}
              />
              {withDescription && (
                <TextArea
                  readonly={disabled}
                  label={translations.description.label}
                  value={form.values.description || ''}
                  error={form.errors.description}
                  onChange={(value) =>
                    form.setFieldValue('description', value, form.submitCount > 0)
                  }
                  placeholder={translations.description.placeholder}
                />
              )}
              <Box maxWidth="s240">
                <QuestionTypeSelector
                  disabled={disabled}
                  translations={translations}
                  isOpen={isTypeSelectionOpen}
                  onToggle={toggleTypeSelection}
                  error={form.errors.answerType}
                  selection={getQuestionTypeSelector(form.values)}
                  onSelection={handleAnswerTypeChange}
                />
              </Box>
              {isQuestionChoice(form.values) && (
                <MultipleChoiceConfiguration
                  disabled={disabled}
                  translations={translations.multiple_choice_configurator}
                  key={getQuestionTypeSelector(form.values)}
                  errors={form.errors.choiceOptions}
                  choices={form.values.choiceOptions}
                  onChange={(value) => form.setFieldValue('choiceOptions', value)}
                />
              )}
              <Box flexDirection="row" alignItems="center" justifyContent="spaceBetween">
                <Box gap="s16" flexDirection="row" alignItems="center">
                  <TogglerCheckbox
                    disabled={disabled}
                    value={form.values.mandatory}
                    onChange={(value) => form.setFieldValue('mandatory', value)}
                    labelBefore
                    label={translations.mandatory}
                  />
                  {isQuestionRating(form.values) && (
                    <TogglerCheckbox
                      disabled={disabled}
                      value={form.values.withComment || false}
                      onChange={(value) => form.setFieldValue('withComment', value)}
                      labelBefore
                      label={translations.optional_comment.label}
                      information={translations.optional_comment.information}
                    />
                  )}
                </Box>
                <Box flexDirection="row" alignItems="center" gap="s8">
                  <Button
                    iconName="delete"
                    type="negative"
                    title={translations.actions.delete}
                    aria-label={translations.actions.delete}
                    onClick={() => onDelete?.(form.values)}
                    disabled={disabled}
                  />
                  <Button
                    iconName="check"
                    type="positive"
                    onClick={() => form.handleSubmit()}
                    disabled={disabled}
                    aria-label={i18n.t(
                      'performance_process.reviews.questionnaires_by_strategy.questions.save_aria_label'
                    )}
                  >
                    {translations.save}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Form>
        </Box>
      )}
    </>
  )
}

export default Question
